import { CenterLine, Point2 } from '../centerline';
import { LevelDef, Pad, Placement, PropDef, TrackDef } from '../level';
import { ZIELONKA_JUMP_MARKS, ZIELONKA_LOOP, ZIELONKA_OVAL, ZIELONKA_WIDTHS } from './zielonka.trace';
import { FeatureDef, featureLength } from '../features';

/**
 * MX Zielonka, Nadma (05-270): the real track from open data (plan 10) and the owner's aerial images (spec
 * docs/superpowers/specs/2026-10-01-zielonka-real-trace-design.md). Ground heights and ground classes come from
 * public/terrain/zielonka.{json,bin} (scripts/terrain/zielonka.mjs); the lap, its widths, the training oval and the
 * owner's jump marks from his georeferenced images (scripts/terrain/trace-zielonka-ref.mjs -> zielonka.trace.ts).
 *
 * Frame: origin = centre of the north training area (the oval and the figure-eight the school uses), north = +z,
 * west = +x. Height 0 is the training ground.
 *
 * The lap starts on the east lane heading north and turns west along the top straight, as the owner's arrows show.
 * Features: the owner's 13 earth jumps (ZIELONKA_JUMPS, one row per jump, numbered in lap order) and a berm with a
 * lip of pushed-out dirt on every hairpin tighter than 10 m radius (from the traced curvature). Worn ruts run along
 * the riding line.
 */

/** A straight drill lane: starts at `start`, runs `length` metres along start.heading, `halfWidth` either side. */
export interface DrillLane { start: Placement; length: number; halfWidth: number }

/** Lane coordinates of a world point: metres along the lane from its start and to its left (+) or right. */
export function laneLocal(lane: DrillLane, x: number, z: number): { along: number; side: number } {
  const h = lane.start.heading, dx = x - lane.start.x, dz = z - lane.start.z;
  return { along: dx * Math.sin(h) + dz * Math.cos(h), side: dx * Math.cos(h) - dz * Math.sin(h) };
}

/** World point `along` metres down the lane and `side` metres to its left. */
export function lanePoint(lane: DrillLane, along: number, side = 0): Point2 {
  const h = lane.start.heading;
  return [lane.start.x + along * Math.sin(h) + side * Math.cos(h), lane.start.z + along * Math.cos(h) - side * Math.sin(h)];
}

/**
 * Figure eight through the crossing midway between two cones: circles of radius r round points `lobeOffset` from
 * the crossing along the cone axis (the cones by default), joined by the two internal tangents. Starts at the
 * crossing heading along `figureEightStartHeading`. Closed, ~2 m point spacing.
 */
export function figureEightPoints(c0: Point2, c1: Point2, r = 5, lobeOffset?: number): Point2[] {
  const ax = c1[0] - c0[0], az = c1[1] - c0[1], l = Math.hypot(ax, az);
  const A = [ax / l, az / l], P = [A[1]!, -A[0]!];
  const mx = (c0[0] + c1[0]) / 2, mz = (c0[1] + c1[1]) / 2;
  // Local frame: u across the axis (P), w along it (A).
  const toWorld = (u: number, w: number): Point2 => [mx + u * P[0]! + w * A[0]!, mz + u * P[1]! + w * A[1]!];
  const c = lobeOffset ?? l / 2;
  const half = Math.sqrt(c * c - r * r), th = Math.asin(r / c);
  const du = half * Math.sin(th), dw = half * Math.cos(th);
  const pts: Point2[] = [];
  const seg = (au: number, aw: number, bu: number, bw: number) => {
    const n = Math.ceil(Math.hypot(bu - au, bw - aw) / 2);
    for (let i = 0; i < n; i++) pts.push(toWorld(au + ((bu - au) * i) / n, aw + ((bw - aw) * i) / n));
  };
  const arc = (wc: number, a0: number, a1: number) => {
    const n = Math.ceil((Math.abs(a1 - a0) * r) / 2);
    for (let i = 0; i < n; i++) { const a = a0 + ((a1 - a0) * i) / n; pts.push(toWorld(r * Math.cos(a), wc + r * Math.sin(a))); }
  };
  seg(0, 0, du, dw);
  const a1 = Math.atan2(dw - c, du);
  arc(c, a1, Math.PI - a1);
  seg(-du, dw, 0, 0);
  seg(0, 0, du, -dw);
  const b1 = Math.atan2(c - dw, du);
  arc(-c, b1, Math.PI - b1 - 2 * Math.PI);
  seg(-du, -dw, 0, 0);
  return pts;
}

/** Heading of the first leg of figureEightPoints (across the cone axis, towards +u). */
export const figureEightStartHeading = (c0: Point2, c1: Point2) => Math.atan2(c1[1] - c0[1], -(c1[0] - c0[0]));

const EAST = -Math.PI / 2, WEST = Math.PI / 2;
// The two straights of the training oval (ZIELONKA_OVAL): the south one is 50 m long, the north one 35 m. The
// throttle lane runs on the south straight, its gate straddling the oval's line (the lap's top straight runs 12 m
// south of it); the brake lane needs 70 m, so it runs along the north straight from beyond the oval's west end.
const OVAL_NORTH_Z = -7, OVAL_SOUTH_Z = -30.3;
const THROTTLE_LANE: DrillLane = { start: { x: -15, z: OVAL_SOUTH_Z, heading: WEST }, length: 30, halfWidth: 2 };
const BRAKE_LANE: DrillLane = { start: { x: 34, z: OVAL_NORTH_Z, heading: EAST }, length: 70, halfWidth: 3 };
const CONES: [Point2, Point2] = [[-6, 15], [10, 15]]; // inside the figure-eight loops, 16 m apart as before

/**
 * The drills on the school's training area (ids kept for saves): throttle on the oval's south straight, brake along
 * its north straight, the figure eight on the flat ground north of the oval.
 */
export const ZIELONKA_DRILLS = {
  /** Accelerate 30 m from the start line to the gate. */
  throttle: {
    ...THROTTLE_LANE,
    gate: [lanePoint(THROTTLE_LANE, 30, 2), lanePoint(THROTTLE_LANE, 30, -2)] as [Point2, Point2],
  },
  /** Reach 40 km/h and stop as close to the line as possible. */
  brake: {
    ...BRAKE_LANE,
    line: [lanePoint(BRAKE_LANE, 70, 2), lanePoint(BRAKE_LANE, 70, -2)] as [Point2, Point2],
  },
  /** Two figure eights round two cones, starting at the crossing point. */
  figure8: {
    start: { x: (CONES[0][0] + CONES[1][0]) / 2, z: (CONES[0][1] + CONES[1][1]) / 2, heading: figureEightStartHeading(...CONES) } as Placement,
    cones: CONES as Point2[],
  },
};

const d = ZIELONKA_DRILLS;

/** Where the paddock stands: the nearest open, flat ground on the east edge to the marker (52.35177 N 21.16001 E),
 * which itself falls on the track; facing west over the start (the east lane, 20 m away). The content paddock (van,
 * stands, coach) anchors here. */
export const ZIELONKA_PADDOCK_ANCHOR: Placement = { x: -79, z: -184, heading: WEST };

/** In the training area, looking north across the oval at the figure eight. */
const TRAINING_VIEW: Placement = { x: 2, z: -21, heading: 0 };

/** Free ride on the training oval: on its south straight, heading east (anticlockwise seen from above). */
const OVAL_START: Placement = { x: 20, z: -30.6, heading: EAST };

const laneCones = (lane: DrillLane, to: number, every: number): PropDef[] => {
  const out: PropDef[] = [];
  for (let a = 0; a <= to + 1e-9; a += every) for (const s of [lane.halfWidth, -lane.halfWidth]) { const [x, z] = lanePoint(lane, a, s); out.push({ kind: 'cone', x, z }); }
  return out;
};

/**
 * Pads: [0] the paddock (the view's clearing and scatter read pads[0]), then the graded clay lanes, then the
 * training ground (oval and figure-eight area) flattened to height 0.
 */
const PADS: Pad[] = [
  { ...ZIELONKA_PADDOCK_ANCHOR, radius: 10, height: 1.53, surface: 'sand' },
  { x: 0, z: OVAL_SOUTH_Z, length: 36, heading: WEST, radius: 2.8, height: 0, surface: 'clay' },
  { x: -1, z: OVAL_NORTH_Z, length: 76, heading: WEST, radius: 3.8, height: 0, surface: 'clay' },
  { x: 3.5, z: -18.6, length: 50, heading: WEST, radius: 16, height: 0, surface: 'sandClay' },
  { x: 2, z: 15, radius: 28, height: 0, surface: 'sandClay' },
];

const DRILL_PROPS: PropDef[] = [
  ...laneCones(THROTTLE_LANE, 25, 5),
  { kind: 'pole', x: d.throttle.gate[0][0], z: d.throttle.gate[0][1] },
  { kind: 'pole', x: d.throttle.gate[1][0], z: d.throttle.gate[1][1] },
  ...laneCones(BRAKE_LANE, 70, 10),
  { kind: 'line', a: d.brake.line[0], b: d.brake.line[1] },
  ...d.figure8.cones.map(([x, z]): PropDef => ({ kind: 'cone', x, z })),
];

/** Tyre ruts worn into the training lines by the school's laps (mirrored about the line). */
const TRAINING_RUTS = { offsets: [0.5], depth: 0.03, width: 0.25 };

/**
 * The owner's 13 earth jumps (docs/research/zielonka-ref/closeup-jumps-yellow.webp: one jump per yellow mark),
 * numbered in lap order from the start. Row n is centred on ZIELONKA_JUMP_MARKS[n - 1], the lap station of the
 * middle of its mark. Edit `height` (m) and `refSpeed` (m/s, the approach speed that clears it, held from 30 m
 * before) by number.
 *
 * Type from the context (spec): a straight gets a tabletop (1.5-2.5 m), a turn or the serpentine a roller
 * (0.8-1.2 m).
 */
export interface JumpRow { n: number; type: 'tabletop' | 'roller'; height: number; refSpeed: number; where: string }
export const ZIELONKA_JUMPS: readonly JumpRow[] = [
  { n: 1, type: 'tabletop', height: 2, refSpeed: 12, where: 'east lane, 80 m out of the east hairpin' },
  { n: 2, type: 'tabletop', height: 1.5, refSpeed: 10, where: 'top-loop return, 30 m out of the top hairpin' },
  { n: 3, type: 'tabletop', height: 1.5, refSpeed: 10.5, where: 'lowest serpentine lane, end of its 60 m straight' },
  { n: 4, type: 'roller', height: 1, refSpeed: 8, where: 'middle serpentine lane' },
  { n: 5, type: 'tabletop', height: 1.5, refSpeed: 9.5, where: 'serpentine diagonal, 30 m out of a hairpin' },
  { n: 6, type: 'tabletop', height: 1.5, refSpeed: 10, where: 'west lane, out of the north-west corner' },
  { n: 7, type: 'roller', height: 1, refSpeed: 9, where: 'south-west sand, bend' },
  { n: 8, type: 'roller', height: 1, refSpeed: 9, where: 'west sand lane, bend' },
  { n: 9, type: 'roller', height: 1, refSpeed: 9, where: 'west sand lane, bend' },
  { n: 10, type: 'roller', height: 0.8, refSpeed: 8, where: 'bottom loop' },
  { n: 11, type: 'tabletop', height: 1.5, refSpeed: 10.5, where: 'north lane out of the bottom loop' },
  { n: 12, type: 'tabletop', height: 1.8, refSpeed: 11, where: 'long V, climbing lane' },
  { n: 13, type: 'tabletop', height: 1.8, refSpeed: 11, where: 'grass-loop east leg' },
];

/** Shapes per type, scaled by height: kicked tabletop faces, a smooth roller. */
const TABLE = { upPerM: 4, top: 2, downPerM: 5, kick: 0.2 };
const ROLLER_LENGTH_PER_M = 9;

/** The jump table as track features, at whole-metre stations. */
export function jumpFeatures(rows: readonly JumpRow[], marks: readonly number[]): FeatureDef[] {
  return rows.map((r): FeatureDef => {
    const m = marks[r.n - 1]!;
    if (r.type === 'roller') {
      const length = ROLLER_LENGTH_PER_M * r.height;
      return { kind: 'roller', s: Math.round(m - length / 2), length, height: r.height, refSpeed: r.refSpeed };
    }
    const up = TABLE.upPerM * r.height, down = TABLE.downPerM * r.height;
    return { kind: 'tabletop', s: Math.round(m - up - TABLE.top / 2), up, top: TABLE.top, down, height: r.height, refSpeed: r.refSpeed, kick: TABLE.kick };
  });
}

/**
 * A berm on every hairpin tighter than 10 m radius: over the stretch tighter than 14 m plus 3 m either side, 1 m
 * high (1.2 m under 7 m radius), with a lip; cut back where a jump or the previous berm is.
 */
export function hairpinBerms(points: Point2[], jumps: readonly FeatureDef[]): FeatureDef[] {
  const line = new CenterLine(points, 1), n = line.count, r = new Float64Array(n);
  for (let i = 0; i < n; i++) r[i] = 1 / Math.max(1e-6, Math.abs(line.curvatureAt(i * line.step, 3)));
  const GAP = 3.5; // features keep 3 m apart (src/sim/level.ts)
  let i0 = 0;
  while (i0 < n && r[i0]! < 14) i0++; // scan from a point outside any turn, so none wraps round the scan's end
  const out: FeatureDef[] = [];
  for (let k = 0; k < n; k++) {
    if (r[(i0 + k) % n]! >= 14) continue;
    let j = k, min = Infinity;
    while (j < n && r[(i0 + j) % n]! < 14) { min = Math.min(min, r[(i0 + j) % n]!); j++; }
    if (min < 10) {
      let a = (i0 + k) * line.step - 3, b = (i0 + j) * line.step + 3;
      for (const f of jumps) {
        const f0 = f.s, f1 = f.s + featureLength(f);
        if (f1 + GAP > a && f0 - GAP < b) { if (f0 - a > b - f1) b = Math.min(b, f0 - GAP); else a = Math.max(a, f1 + GAP); }
      }
      const prev = out[out.length - 1];
      if (prev) a = Math.max(a, prev.s + featureLength(prev) + GAP); // the next bend of an S starts its own berm
      if (b - a > 6) out.push({ kind: 'berm', s: a, length: b - a, height: min < 7 ? 1.2 : 1, lip: 0.25 });
    }
    k = j;
  }
  // Whole metres (feature ids and how-to clip names carry the station).
  return out.map((f) => { const s = Math.ceil(line.wrap(f.s)); return { ...f, s, length: Math.floor(f.s + featureLength(f) - s) }; });
}

const JUMPS = jumpFeatures(ZIELONKA_JUMPS, ZIELONKA_JUMP_MARKS);

/** Section widths: the measured ribbon width every 5 m (zielonka.trace.ts), clamped there to 4-9 m. */
const TRACK: TrackDef = {
  points: ZIELONKA_LOOP,
  width: 4.5, // nominal; sections use `widths`
  widths: ZIELONKA_WIDTHS,
  blend: 6,
  surface: 'sandClay',
  spans: [],
  grooves: { offsets: [-1.1, 1.1], depth: 0.03, width: 0.4 },
  features: [...JUMPS, ...hairpinBerms(ZIELONKA_LOOP, JUMPS)].sort((a, b) => a.s - b.s),
};

/** The start 12 m up the east lane (s = 0 is the east hairpin's exit), the gate 6 m ahead of it. */
const START_S = 12, GATE_S = 18;

/** Half the measured width at arc length s (linear between the 5 m samples, round the loop). */
function halfWidthAt(track: TrackDef, line: CenterLine, s: number): number {
  const w = track.widths!, u = line.wrap(s);
  let k = w.findIndex(([ws]) => ws > u);
  if (k < 0) k = w.length;
  const [s0, w0] = w[k - 1]!, [s1, w1] = k < w.length ? w[k]! : [line.length, w[0]![1]];
  return (w0 + ((w1 - w0) * (u - s0)) / Math.max(1e-6, s1 - s0)) / 2;
}

/**
 * Track furniture: a start gate (a post every metre across the track and a line) 6 m ahead of the start, and
 * straw bales on the outside of every berm, 3 m beyond the track edge (clear of the berm's lip).
 */
function trackScenery(track: TrackDef): PropDef[] {
  const line = new CenterLine(track.points), out: PropDef[] = [];
  const hw = halfWidthAt(track, line, GATE_S);
  const at = (s: number, side: number): Point2 => {
    const p = line.pointAt(s), t = line.tangentAt(s);
    return [p.x - t.z * side, p.z + t.x * side];
  };
  for (let d = -hw; d <= hw + 1e-9; d += 1) { const [x, z] = at(GATE_S, d); out.push({ kind: 'pole', x, z }); }
  out.push({ kind: 'line', a: at(GATE_S - 0.5, -hw), b: at(GATE_S - 0.5, hw) });
  for (const f of track.features) {
    if (f.kind !== 'berm') continue;
    const mid = f.s + f.length / 2;
    // Outside of the turn: a left turn (curvature > 0) is bermed on the right (d < 0).
    const side = line.curvatureAt(mid, f.length / 3) > 0 ? -1 : 1;
    for (const u of [-3, 0, 3]) {
      // Clear of every part of the lap: inside hairpins another leg can pass within a few metres.
      const s = mid + u, h = halfWidthAt(track, line, s), [x, z] = at(s, side * (h + 3)), t = line.tangentAt(s);
      const c = line.closest(x, z);
      if (Math.abs(c.d) < halfWidthAt(track, line, c.s) + 1.5) continue;
      out.push({ kind: 'hay', x, z, heading: Math.atan2(t.x, t.z) });
    }
  }
  return out;
}

/**
 * Man-made relief of the lap. No usable elevation source resolves it (Geoportal's 1 m LiDAR is unreachable from
 * the build machine, Copernicus is 30 m), so the mounds are modelled from the orthophoto: shadow lines north of
 * raised banks (sun from the south) and bare-sand heaps, with heights typical of sand MX tracks round Warsaw.
 * Mounds are part of the ground, so neighbouring ribbons 10-15 m apart share consistent slopes. The training area
 * stays flat (its pads flatten over them).
 */
const RELIEF: LevelDef['hills'] = [
  // Bank between the serpentine's middle and lowest lanes: the shadow line on its north side (x 0-30, z -110) in
  // the owner's close-up. Its crest is just north of the lowest lane; it ends short of the west hairpin (x 52).
  { x: 15, z: -112, radius: 4, height: 2, length: 50, heading: Math.PI / 2 },
  // Divider mound between the top straight (z -42) and the top loop's return lane (z -55): dark streaks between them.
  { x: 0, z: -48.5, radius: 2.5, height: 1.8, length: 50, heading: Math.PI / 2 }, // ends 13 m short of the top hairpin (x 38)
  // Central sand hill inside the middle serpentine: bare-sand patch, lanes wrap its flanks.
  { x: -5, z: -178, radius: 12, height: 4 },
  // South hill under the bottom loop; the west lane is its long downhill.
  { x: 8, z: -242, radius: 13, height: 4 },
  // East hill at the east hairpin.
  { x: -48, z: -185, radius: 10, height: 3 },
  // North-west knoll at the top hairpin.
  { x: 75, z: -62, radius: 9, height: 2.5 },
];

/** The shared world: real ground, forest where the orthophoto shows forest, the lap and the training loops. */
const WORLD: Omit<LevelDef, 'id' | 'name' | 'starts' | 'checkpoints'> = {
  seed: 505,
  ground: { amplitude: 0, scale: 60, surface: 'grass' },
  hills: RELIEF,
  pads: PADS,
  track: TRACK,
  forest: { corridor: 6, spacing: 4, density: 0.6, minRadius: 0.12, maxRadius: 0.3 },
  terrain: {
    name: 'zielonka',
    surfaces: { forest: 'roots', grass: 'grass', heath: 'grass', dirt: 'sandClay', sand: 'sand', road: 'sandClay', water: 'grass' }, // ponds as wet grass: a 6th ground layer overflows the 16 fragment samplers
    trees: { forest: 0.6, heath: 0.05, grass: 0.02 },
  },
  // The training oval (traced on the owner's overview; paths[0], the free-ride line) and the figure eight ridden
  // round the drill cones, as hard-packed riding lines.
  paths: [
    { points: ZIELONKA_OVAL, width: 5, surface: 'clay' as const, grooves: TRAINING_RUTS, lip: 0.12 },
    { points: figureEightPoints(...CONES), width: 4, surface: 'clay' as const, grooves: TRAINING_RUTS, lip: 0.1 },
  ],
  paddock: ZIELONKA_PADDOCK_ANCHOR,
  props: DRILL_PROPS,
  scenery: trackScenery(TRACK),
  courseTape: true,
};

/** The training area (progress id 'ring'): free ride on the oval and the three drills. */
export const ZIELONKA_PADDOCK: LevelDef = {
  ...WORLD,
  id: 'ring',
  name: 'Zielonka paddock',
  map: 'drills',
  starts: { default: TRAINING_VIEW, oval: OVAL_START, throttle: d.throttle.start, brake: d.brake.start, figure8: d.figure8.start },
  checkpoints: [],
};

/** Laps of the real loop (progress id 'track'): six checkpoints about 230 m apart from the hairpin exit (s = 0). */
export const ZIELONKA: LevelDef = {
  ...WORLD,
  id: 'track',
  name: 'MX Zielonka',
  starts: { default: { s: START_S } },
  checkpoints: [0, 230, 460, 690, 920, 1150],
};
